import { formatLongDate } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { skillLabelFor } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/format';
import { letterFor } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/optionLetters';
import {
  AnsweredQuestion,
  correctIndexOf,
} from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/quizScoring';
import { DailyQuestion } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

type QuizAgentContextArgs = Readonly<{
  dayDate: string;
  /** The day's questions with the student's choice, in presented order; empty when unavailable. */
  answered: readonly AnsweredQuestion[];
  /** True once the attempt is graded and results are shown; false while the quiz is in progress. */
  completed: boolean;
  score: number;
  total: number;
}>;

const STRINGS = appStrings.modules.satActBooster.dailyPractice.quiz;

const optionLabel = (question: DailyQuestion, index: number | null): string | null => {
  if (index === null) return null;

  const option = question.options[index];

  return option ? `${STRINGS.optionLabel(letterFor(index))} ${option.text}` : null;
};

/**
 * One question for the agent. The answer key — the correct answer, each option's verdict and reason,
 * and the overall note — is withheld until the student has answered, since the chat sits beside the
 * quiz and anything sent here can be asked for mid-question.
 */
const questionContext = (entry: AnsweredQuestion, completed: boolean) => {
  const revealKey = completed || entry.chosenIndex !== null;

  return {
    skill: skillLabelFor(entry.question),
    stem: entry.question.stem,
    yourAnswer: optionLabel(entry.question, entry.chosenIndex),
    result: entry.chosenIndex === null ? 'unanswered' : entry.correct ? 'correct' : 'incorrect',
    options: entry.question.options.map((option, index) => ({
      label: STRINGS.optionLabel(letterFor(index)),
      text: option.text,
      ...(revealKey && { isCorrect: option.isCorrect, whyRightOrWrong: option.explanation }),
    })),
    ...(revealKey && {
      correctAnswer: optionLabel(entry.question, correctIndexOf(entry.question)),
      explanation: entry.question.explanation,
    }),
  };
};

/**
 * Give Lubav the actual practice quiz — stems, the student's answers, and the reason each option is
 * right or wrong once it can be shown — so it can discuss why a specific wrong answer was wrong
 * rather than only the score. Registers nothing until the questions are loaded.
 */
export const useQuizAgentContext = ({
  dayDate,
  answered,
  completed,
  score,
  total,
}: QuizAgentContextArgs) => {
  useRegisterAgentContext(
    answered.length
      ? {
          kind: 'visual',
          label: 'Daily practice quiz',
          display: completed
            ? `The student finished this day's practice quiz, scoring ${score} of ${total}.`
            : "The student is working through this day's practice quiz.",
          detail:
            'Ground any quiz discussion in these exact questions and the student’s answers — do not invent questions, and never guess an answer key you weren’t given.',
          data: {
            date: formatLongDate(dayDate),
            status: completed ? 'completed' : 'in-progress',
            score,
            total,
            questions: answered.map((entry) => questionContext(entry, completed)),
          },
        }
      : null,
  );
};
